// Cobrança dos valores vencidos: agrupa por cliente e prepara a mensagem de WhatsApp.
// Nada é enviado sozinho: a advogada revisa e envia pelo próprio WhatsApp.

export interface ChargeEntry {
  id: string;
  description?: string | null;
  amount?: number | string | null;
  entry_type?: string | null;
  status?: string | null;
  payment_date?: string | null;
  due_date?: string | null;
  client_id?: string | null;
  notes?: string | null;
  charge_sent_at?: string | null;
  cases?: { client_id?: string | null } | null;
}

export interface ChargeClient {
  id: string;
  name?: string | null;
  mobile?: string | null;
  phone?: string | null;
  alternative_phone?: string | null;
}

export interface ChargeGroup {
  key: string;
  clientId: string | null;
  name: string;
  phone: string;
  entries: ChargeEntry[];
  total: number;
  oldestDate: string;
  daysLate: number;
  lastChargeAt: string | null;
}

export const localDateToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const dateOf = (entry: ChargeEntry) => String(entry.payment_date || entry.due_date || '').slice(0, 10);
const dayNumber = (date: string) => Date.parse(`${date}T12:00:00Z`) / 86400000;

export const isOverdueCharge = (entry: ChargeEntry, today: string) => {
  const date = dateOf(entry);
  return entry.entry_type === 'receita' && !/pago/i.test(String(entry.status || '')) && !!date && date < today;
};

/** Telefone escrito na observação, para quem pagou sem ser cliente */
export const phoneFromNotes = (notes?: string | null) => {
  const match = String(notes || '').match(/\(?\b\d{2}\)?[\s.-]*9?\d{4}[\s.-]?\d{4}\b/);
  return match ? match[0].trim() : '';
};

export const phoneDigits = (phone: string) => phone.replace(/\D/g, '');
export const isUsablePhone = (phone: string) => {
  const digits = phoneDigits(phone).replace(/^55(?=\d{10,11}$)/, '');
  return digits.length === 10 || digits.length === 11;
};

export const buildChargeGroups = (entries: ChargeEntry[], clients: ChargeClient[], today: string): ChargeGroup[] => {
  const clientById = new Map(clients.map((c) => [c.id, c]));
  const groups = new Map<string, ChargeGroup>();

  entries.filter((e) => isOverdueCharge(e, today)).forEach((entry) => {
    const clientId = entry.client_id || entry.cases?.client_id || null;
    const client = clientId ? clientById.get(clientId) : undefined;
    const key = clientId || `avulso-${entry.id}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        clientId,
        name: client?.name || 'Sem cliente vinculado',
        phone: client?.mobile || client?.phone || client?.alternative_phone || phoneFromNotes(entry.notes),
        entries: [],
        total: 0,
        oldestDate: dateOf(entry),
        daysLate: 0,
        lastChargeAt: null,
      });
    }
    const group = groups.get(key)!;
    group.entries.push(entry);
    group.total += Number(entry.amount) || 0;
    if (dateOf(entry) < group.oldestDate) group.oldestDate = dateOf(entry);
    if (entry.charge_sent_at && (!group.lastChargeAt || entry.charge_sent_at > group.lastChargeAt)) group.lastChargeAt = entry.charge_sent_at;
  });

  return [...groups.values()]
    .map((g) => ({
      ...g,
      entries: g.entries.slice().sort((a, b) => dateOf(a).localeCompare(dateOf(b))),
      daysLate: Math.round(dayNumber(today) - dayNumber(g.oldestDate)),
    }))
    .sort((a, b) => a.oldestDate.localeCompare(b.oldestDate));
};

export const formatMoney = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDate = (date: string) => date.split('-').reverse().join('/');

const firstName = (name: string) => {
  const first = name.trim().split(/\s+/)[0] || '';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

export const chargeMessage = (group: ChargeGroup) => {
  const greeting = group.clientId ? `Olá, ${firstName(group.name)}! Tudo bem?` : 'Olá! Tudo bem?';
  const lines = group.entries.map((e) => `• ${String(e.description || 'Honorários').replace(/\s+/g, ' ').trim()}: ${formatMoney(Number(e.amount) || 0)}, vencimento em ${formatDate(dateOf(e))}`);
  const single = group.entries.length === 1;
  return [
    greeting,
    '',
    `Passando para lembrar que consta${single ? '' : 'm'} em aberto:`,
    ...lines,
    ...(single ? [] : ['', `Total: ${formatMoney(group.total)}`]),
    '',
    'Se já tiver feito o pagamento, por favor, me envie o comprovante e desconsidere esta mensagem.',
    '',
    'Um abraço,',
    'Allana Sthéfany',
  ].join('\n');
};

export const chargeWhatsappLink = (phone: string, message: string) => {
  const digits = phoneDigits(phone);
  const full = digits.startsWith('55') && digits.length > 11 ? digits : `55${digits}`;
  return `https://wa.me/${full}?text=${encodeURIComponent(message)}`;
};
